import { EngagementStatus, PaymentStatus, Role } from "@gigsync/protocol";
import { localDay, recurs } from "../job/when.js";
import { OPEN_PAYMENT } from "./book.js";
const S = PaymentStatus.enum;
const E = EngagementStatus.enum;
const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;
/** `Date.getUTCDay()` counts from Sunday; the passbook's week starts on Monday. */
const MONDAY = 1;
/** Length of "YYYY-MM" at the start of a "YYYY-MM-DD" day. */
const MONTH_LENGTH = 7;
/** Work that is finished, so money is due whether or not anyone has asked for it. */
export const FINISHED_WORK = new Set([E.delivered, E.closed]);
/** The calendar month ("YYYY-MM") of a "YYYY-MM-DD" day. */
export const monthOf = (day) => day.slice(0, MONTH_LENGTH);
/** Both sides signed the same terms: only then is a job GigSync evidence, whichever side this phone was on. */
export function bothSigned(r) {
    return r.mine !== undefined && r.theirs !== undefined && r.mine.terms_hash === r.theirs.terms_hash;
}
/** Signed work that is this phone's own: it was the provider. */
const signedWork = (r) => r.engagement.role === Role.enum.provider && bothSigned(r);
/** This phone's own work on its passbook: signed by both, or refilled from its record on GigSync. */
const ownWork = (r) => signedWork(r) || (r.engagement.role === Role.enum.provider && !!r.restored);
/** The price both agreed: from the signed terms, or from the record for a restored job. */
export const agreedPrice = (r) => r.mine?.terms.price_paise ?? r.restored?.price_paise ?? 0;
/** Work this phone did and finished, on terms both sides signed: what the mentor rule counts. */
export const doneWork = (r) => signedWork(r) && FINISHED_WORK.has(r.engagement.status);
const filedAt = (p, r) => new Date(p.confirmed_at ?? p.paid_at ?? p.requested_at ?? r.engagement.created_at);
/** The first and last day ("YYYY-MM-DD") of the local week around `now`. */
function weekOf(now, offsetMinutes) {
    const today = new Date(`${localDay(now, offsetMinutes)}T00:00:00Z`);
    const sinceMonday = (today.getUTCDay() - MONDAY + DAYS_PER_WEEK) % DAYS_PER_WEEK;
    const first = new Date(today.getTime() - sinceMonday * MS_PER_DAY);
    const last = new Date(first.getTime() + (DAYS_PER_WEEK - 1) * MS_PER_DAY);
    return { first: localDay(first, 0), last: localDay(last, 0) };
}
/** Rows newest first, and the totals, for the phone `offsetMinutes` ahead of UTC. */
export function passbook(jobs, payments, me, now, offsetMinutes) {
    const signed = new Map(jobs.filter(ownWork).map((r) => [r.engagement.id, r]));
    const rows = [];
    for (const payment of payments) {
        const job = signed.get(payment.engagement);
        if (job && payment.payee === me)
            rows.push({ job, payment, at: filedAt(payment, job) });
    }
    rows.sort((a, b) => b.at.getTime() - a.at.getTime() || b.payment.id.localeCompare(a.payment.id));
    const week = weekOf(now, offsetMinutes);
    const month = monthOf(localDay(now, offsetMinutes));
    const totals = { week: 0, month: 0, waiting: 0, notAsked: 0 };
    for (const { payment: p } of rows) {
        if (OPEN_PAYMENT.has(p.status))
            totals.waiting += p.amount_paise;
        if (p.status !== S.confirmed || !p.confirmed_at)
            continue;
        const day = localDay(new Date(p.confirmed_at), offsetMinutes);
        if (day >= week.first && day <= week.last)
            totals.week += p.amount_paise;
        if (monthOf(day) === month)
            totals.month += p.amount_paise;
    }
    const asked = new Set(rows.filter((row) => row.payment.status !== S.disputed).map((row) => row.job.engagement.id));
    for (const job of signed.values()) {
        if (FINISHED_WORK.has(job.engagement.status) && !asked.has(job.engagement.id))
            totals.notAsked += agreedPrice(job);
    }
    return { rows, totals };
}
/** Why the worker may ask again on a job already asked for: after a dispute, or for a new month of monthly work. */
export const AskAgain = { dispute: "dispute", month: "month" };
/** Monthly work is asked for again once the last confirmed payment is this old. */
const MONTHLY_REPEAT_DAYS = 28;
/** Milliseconds since the epoch, or 0 for a missing stamp; offsets differ between phones, so never compare the text. */
const ms = (at) => (at ? Date.parse(at) : 0);
/** Undefined when asking again is not offered, e.g. a one-off job that has been paid. */
export function askAgain(job, payments, now) {
    const mine = payments
        .filter((p) => p.engagement === job.engagement.id)
        .sort((a, b) => ms(b.requested_at) - ms(a.requested_at) || b.id.localeCompare(a.id));
    if (mine[0]?.status === S.disputed)
        return AskAgain.dispute;
    if (!recurs(job.request) || mine.some((p) => OPEN_PAYMENT.has(p.status)))
        return undefined;
    const confirmed = mine.filter((p) => p.status === S.confirmed).map((p) => ms(p.confirmed_at));
    if (confirmed.length === 0)
        return undefined;
    return now.getTime() - Math.max(...confirmed) >= MONTHLY_REPEAT_DAYS * MS_PER_DAY ? AskAgain.month : undefined;
}
//# sourceMappingURL=passbook.js.map